import { syntaxHighlightingThemeNames } from './themes'

import type { BundledLanguage, BundledTheme, Highlighter } from 'shiki/bundle/web'

const languages: BundledLanguage[] = [
    'bash',
    'css',
    'html',
    'javascript',
    'json',
    'jsx',
    'markdown',
    'shellscript',
    'tsx',
    'typescript',
    'yaml',
]

const themes: BundledTheme[] = [syntaxHighlightingThemeNames.light, syntaxHighlightingThemeNames.dark]

let highlighterPromise: Promise<Highlighter> | null = null

const getHighlighter = async () => {
    if (!highlighterPromise) {
        highlighterPromise = import('shiki/bundle/web').then(({ createHighlighter }) =>
            createHighlighter({
                langs: languages,
                themes,
            })
        )
    }

    return highlighterPromise
}

const normalizeLanguage = (language: string): BundledLanguage | null => {
    switch (language.toLowerCase()) {
        case 'bash':
        case 'sh':
        case 'shell':
        case 'shellscript':
        case 'zsh':
            return 'shellscript'
        case 'css':
            return 'css'
        case 'diff':
            return null
        case 'html':
            return 'html'
        case 'javascript':
        case 'js':
            return 'javascript'
        case 'json':
        case 'jsonc':
            return 'json'
        case 'jsx':
            return 'jsx'
        case 'markdown':
        case 'md':
            return 'markdown'
        case 'tsx':
            return 'tsx'
        case 'typescript':
        case 'ts':
            return 'typescript'
        case 'yaml':
        case 'yml':
            return 'yaml'
        default:
            return null
    }
}

export const highlightCodeToHtml = async ({ code, language }: { code: string; language: string }) => {
    const normalizedLanguage = normalizeLanguage(language)

    if (!normalizedLanguage) {
        return null
    }

    let highlighter: Highlighter

    try {
        highlighter = await getHighlighter()
    } catch {
        highlighterPromise = null
        return null
    }

    return highlighter.codeToHtml(code, {
        lang: normalizedLanguage,
        themes: {
            light: syntaxHighlightingThemeNames.light,
            dark: syntaxHighlightingThemeNames.dark,
        },
        defaultColor: 'light',
    })
}

const copyIcon = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"',
    ' fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"',
    ' stroke-linejoin="round" aria-hidden="true">',
    '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>',
    '<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
    '</svg>',
].join('')

const getCodeLanguage = (codeElement: Element) => {
    for (const className of Array.from(codeElement.classList)) {
        if (className.startsWith('language-')) {
            return className.slice('language-'.length)
        }
    }

    return ''
}

const addCopyButtons = (root: Element, document: Document) => {
    Array.from(root.querySelectorAll('pre')).forEach((preElement, index) => {
        if (preElement.parentElement?.classList.contains('markdown-code-block')) {
            return
        }

        const wrapper = document.createElement('div')
        wrapper.className = 'markdown-code-block'
        wrapper.setAttribute('data-markdown-code-block-id', `${index}`)
        preElement.replaceWith(wrapper)
        wrapper.appendChild(preElement)

        const button = document.createElement('button')
        button.className = 'markdown-code-copy'
        button.type = 'button'
        button.title = 'Copy code'
        button.setAttribute('aria-label', 'Copy code')
        button.setAttribute('data-markdown-copy-code', 'true')
        button.innerHTML = copyIcon
        wrapper.appendChild(button)

        const expandButton = document.createElement('button')
        expandButton.className = 'markdown-code-expand'
        expandButton.type = 'button'
        expandButton.setAttribute('data-markdown-expand-code', 'true')
        wrapper.appendChild(expandButton)
    })
}

export const prepareMarkdownCodeBlocks = async ({
    html,
    syntaxHighlighting,
}: {
    html: string
    syntaxHighlighting?: boolean
}) => {
    if (!html.includes('<pre')) {
        return html
    }

    const parser = new DOMParser()
    const document = parser.parseFromString(`<div>${html}</div>`, 'text/html')
    const root = document.body.firstElementChild

    if (!root) {
        return html
    }

    const codeBlocks = Array.from(root.querySelectorAll('pre > code'))

    if (syntaxHighlighting) {
        await Promise.all(
            codeBlocks.map(async (codeElement) => {
                const language = getCodeLanguage(codeElement)
                const preElement = codeElement.parentElement

                if (!language || !preElement) {
                    return
                }

                const highlightedHtml = await highlightCodeToHtml({
                    code: codeElement.textContent || '',
                    language,
                })

                if (!highlightedHtml) {
                    return
                }

                const template = document.createElement('template')
                template.innerHTML = highlightedHtml.trim()
                const highlightedPreElement = template.content.firstElementChild

                if (!highlightedPreElement) {
                    return
                }

                highlightedPreElement.classList.add('markdown-shiki')
                preElement.replaceWith(highlightedPreElement)
            })
        )
    }

    addCopyButtons(root, document)

    return root.innerHTML
}
